import { Box, Button, Stack, Typography } from '@mui/material';
import { Panel } from '../../components/Panel';
import { StatusDot } from '../../components/StatusChip';
import type { Tone } from '../../components/tone';
import type { LiveStatus } from '../../lib/useLive';
import { EmptyState } from '../../components/EmptyState';
import { useActivity } from '../activity/useActivity';
import { useClaudeTasks } from '../claudeTasks/useClaudeTasks';
import { shortDate } from '../../lib/dates';
import { useAction } from '../../lib/useAction';
import { useBrain } from '../brain/useBrain';
import { formatJordan } from '@serb/shared';

/** What the assistant has been doing, newest first */
export function ActivityPanel() {
  const rows = useActivity()
    .items.slice(0, 6)
    .map((e) => ({
      id: e.id,
      time: e.time || shortDate(e.date),
      text: e.activity + (e.srb ? ` for ${e.srb}` : ''),
      detail: e.details,
    }));
  return (
    <Panel title="SERB Activity" viewAll="/history">
      {rows.length ? (
        <Stack spacing={1.5}>
          {rows.map((a) => (
            <Stack key={a.id} direction="row" spacing={1.25}>
              <Box
                sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.75, flexShrink: 0 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ width: 38, flexShrink: 0, pt: 0.25 }}>
                {a.time}
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{a.text}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {a.detail}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : (
        <EmptyState>Nothing yet.</EmptyState>
      )}
    </Panel>
  );
}

type StatusRow = { label: string; value: string; tone: Tone };

/** Is each part of the system working: the app's own connection, and what Claude has done and still has to do */
export function SystemStatusPanel({ live }: { live: LiveStatus }) {
  const last = useActivity().items.find((e) => e.by === 'Claude');
  const waiting = useClaudeTasks().items.filter((t) => t.status === 'waiting').length;
  const brain = useBrain();
  const wake = useAction(brain.wake, { success: 'Claude is starting. Its result shows here when it finishes.' });
  const rows: StatusRow[] = [
    {
      label: 'App connection',
      value: live === 'live' ? 'Live' : live === 'connecting' ? 'Connecting…' : 'Reconnecting…',
      tone: live === 'live' ? 'success' : live === 'connecting' ? 'neutral' : 'error',
    },
    {
      label: 'Claude on the server',
      value: !brain.enabled
        ? 'Not switched on'
        : brain.running
          ? 'Working…'
          : brain.last
            ? `${brain.last.status === 'ok' ? 'Done' : 'Failed'}, ${formatJordan(brain.last.finishedAt ?? brain.last.startedAt, { weekday: undefined })}`
            : 'Waiting for something to do',
      tone: !brain.enabled
        ? 'neutral'
        : brain.running
          ? 'warning'
          : brain.last?.status === 'failed'
            ? 'error'
            : 'success',
    },
    {
      label: 'Claude last worked',
      value: last ? `${shortDate(last.date)}${last.time ? ` ${last.time}` : ''}` : 'Not yet',
      tone: last ? 'success' : 'neutral',
    },
    {
      label: 'Tasks waiting for Claude',
      value: String(waiting),
      tone: waiting ? 'warning' : 'success',
    },
  ];
  return (
    <Panel title="System Status">
      <Stack spacing={1.25}>
        {rows.map((r) => (
          <Stack key={r.label} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
            <StatusDot tone={r.tone} />
            <Typography sx={{ flex: 1, fontSize: 14 }}>{r.label}</Typography>
            <Typography variant="body2" color={r.tone === 'success' ? 'success.main' : 'text.secondary'}>
              {r.value}
            </Typography>
          </Stack>
        ))}
        {brain.enabled && (
          <>
            {brain.last && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Last run ({brain.last.reasons.join('; ')}): {brain.last.summary.slice(0, 220)}
              </Typography>
            )}
            <Button size="small" variant="outlined" disabled={wake.busy || brain.running} onClick={() => wake.run()}>
              Run Claude now
            </Button>
          </>
        )}
      </Stack>
    </Panel>
  );
}
